/**
 * Panel lateral de una tarea: crear una nueva, o ver una existente (lectura, con edición por campo),
 * sus adjuntos, comentarios e historial.
 *
 * Se divide en dos componentes (`PanelCrear` y `PanelDetalle`) porque cada uno usa hooks de
 * datos distintos, y los hooks no pueden llamarse de forma condicional.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ETIQUETA_ESTADO,
  PRIORIDADES,
  formatearNumero,
  type EventoTarea,
  type Prioridad,
  type TareaDetalle,
  type Usuario,
} from '@cdtec-backlog/contrato';
import { nombreCorto } from '../../componentes/Avatar';
import { Esqueleto, Giro } from '../../componentes/Esqueleto';
import { EstadoError } from '../../componentes/EstadoSeccion';
import { Icono } from '../../componentes/Icono';
import { useMostrarTrasRetardo } from '../../componentes/useMostrarTrasRetardo';
import { ErrorDeApi } from '../../lib/api';
import { fechaHora } from '../../lib/formato';
import { guardarPreferencia, leerPreferencia } from '../../lib/almacenamiento';
import { ListaAdjuntos } from '../adjuntos/ListaAdjuntos';
import { useEtiquetaPrioridad } from '../configuracion/hooks';
import { useCrearTarea, useEliminarTarea, useTareaDetalle } from './hooks';
import { Comentarios } from './Comentarios';
import { FichaCargando, tareaDeLasListas } from './FichaCargando';
import { FormularioTarea, VALORES_NUEVA_TAREA, type ErroresFechas } from './FormularioTarea';
import { IconoTipo } from './IconoTipo';
import { SelectorEstado } from './SelectorEstado';
import { VistaTarea } from './VistaTarea';
import './PanelTarea.css';

/**
 * Frase para mostrar un movimiento del historial. Los eventos de prioridad guardan P0..P3;
 * al mostrarlos se traducen a la etiqueta vigente (el historial guardado no se reescribe).
 */
export function textoDeEvento(
  evento: EventoTarea,
  etiquetaPrioridad: (p: Prioridad) => string = (p) => p,
): string {
  const nombre = (valor: string | null) =>
    valor === null ? '—' : esPrioridad(valor) ? etiquetaPrioridad(valor) : valor;
  switch (evento.tipo) {
    case 'ESTADO':
      // Al descartar, el evento guarda el motivo en `texto`.
      return `Estado: ${evento.de ?? '—'} → ${evento.a ?? '—'}${evento.texto ? ` · «${evento.texto}»` : ''}`;
    case 'PRIORIDAD':
      return `Prioridad: ${nombre(evento.de)} → ${nombre(evento.a)}`;
    case 'RESPONSABLE':
      return `Responsable: ${evento.de ?? '—'} → ${evento.a ?? '—'}`;
    case 'RELACION':
      // Subtareas y bloqueos (ADR-0018): la API ya trae la frase («Ahora es subtarea de CDT-12», «Bloqueada por
      // CDT-9», «Ya no bloquea a CDT-12»). `de`/`a` son números; sin frase, se arma una genérica.
      return evento.texto || `Relación: ${evento.de ?? '—'} → ${evento.a ?? '—'}`;
    default:
      return evento.texto;
  }
}

const FOCALIZABLES =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** Clave de la preferencia «ficha ampliada» (se guarda en el navegador, con try/catch en lib/almacenamiento). */
export const CLAVE_FICHA_AMPLIA = 'ficha-amplia';

function Contenedor({
  titulo,
  izquierda,
  ampliable = false,
  onCerrar,
  children,
}: {
  /** Título de la barra, que además da nombre al diálogo. */
  titulo: ReactNode;
  /** Lo que va junto al título, a su derecha (el estado de la tarea). */
  izquierda?: ReactNode;
  /** Muestra el botón de ampliar (solo la ficha de una tarea; el CSS lo oculta bajo 900 px). */
  ampliable?: boolean;
  onCerrar: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Se lee siempre (no solo si es ampliable): el panel pasa de «Cargando…» a la ficha sin remontarse.
  const [preferida, setAmplia] = useState(() => leerPreferencia(CLAVE_FICHA_AMPLIA) === '1');
  const amplia = ampliable && preferida;
  const alternarAmplia = useCallback(() => {
    setAmplia((actual) => {
      guardarPreferencia(CLAVE_FICHA_AMPLIA, actual ? '0' : '1');
      return !actual;
    });
  }, []);

  // Escape cierra el panel, salvo que otro control (p. ej. un campo en edición) ya lo haya atendido.
  useEffect(() => {
    const alTeclear = (evento: globalThis.KeyboardEvent) => {
      if (evento.key === 'Escape' && !evento.defaultPrevented) onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  // Foco: al abrir entra al panel y al cerrar vuelve a quien lo abrió.
  useEffect(() => {
    const quienAbrio =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const elemento = panel.current;
    (elemento?.querySelector<HTMLElement>('[data-foco-inicial]') ?? elemento)?.focus();
    return () => {
      if (quienAbrio?.isConnected) quienAbrio.focus();
    };
  }, []);

  // Atrapa el foco: Tab y Mayús+Tab dan la vuelta dentro del panel. Los botones ocultos por CSS
  // (ampliar bajo 900 px) no cuentan: no se pueden enfocar.
  const atrapar = (evento: KeyboardEvent) => {
    if (evento.key !== 'Tab' || !panel.current) return;
    const lista = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCALIZABLES)).filter(
      (el) => window.getComputedStyle(el).display !== 'none',
    );
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (!primero || !ultimo) {
      evento.preventDefault();
      return;
    }
    const activo = document.activeElement;
    if (evento.shiftKey && (activo === primero || activo === panel.current)) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && activo === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  };

  return (
    <div
      className="velo"
      onMouseDown={(evento) => evento.target === evento.currentTarget && onCerrar()}
    >
      <div
        ref={panel}
        className={amplia ? 'panel panel--amplia' : 'panel'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-titulo"
        tabIndex={-1}
        onKeyDown={atrapar}
      >
        <div className="panel__barra">
          <h2 id="panel-titulo" className="panel__titulo">
            {titulo}
          </h2>
          {izquierda}
          <div className="panel__acciones">
            {ampliable && (
              <button
                type="button"
                className="panel__icono panel__ampliar"
                aria-label={amplia ? 'Volver a panel lateral' : 'Ampliar vista'}
                title={amplia ? 'Volver a panel lateral' : 'Ampliar vista'}
                onClick={alternarAmplia}
              >
                <Icono nombre={amplia ? 'contraer' : 'expandir'} tamano={18} />
              </button>
            )}
            <button
              type="button"
              className="panel__icono"
              aria-label="Cerrar"
              title="Cerrar (Esc)"
              onClick={onCerrar}
            >
              <Icono nombre="x" tamano={18} />
            </button>
          </div>
        </div>
        <div className="panel__cuerpo">{children}</div>
      </div>
    </div>
  );
}

interface PropsPanel {
  /** Id de la tarea, o `nueva` para crear. */
  id: number | 'nueva';
  personas: Usuario[];
  puedeEscribir: boolean;
  puedeAdministrar: boolean;
  /** Id de la persona con sesión (para saber qué adjuntos son suyos). */
  yoId: number;
  onCerrar: () => void;
  /** Abre otra tarea en el panel (desde «Tareas parecidas» al crear, la madre o una subtarea). */
  onAbrir?: (id: number) => void;
  /** Al crear: la tarea madre ya elegida (`?tarea=nueva&padre=12`, desde «Nueva subtarea»). */
  padreId?: number | null;
  /** «Nueva subtarea» en la ficha de una madre: abre «Nueva tarea» con ella como madre. */
  onNuevaSubtarea?: (madreId: number) => void;
}

export function PanelTarea({ id, ...resto }: PropsPanel) {
  // Otra madre (o ninguna) es otro formulario: sus valores iniciales se leen una sola vez.
  return id === 'nueva' ? (
    <PanelCrear key={resto.padreId ?? 'sin-madre'} {...resto} />
  ) : (
    <PanelDetalle id={id} {...resto} />
  );
}

/** Errores de la API que son de una fecha, para mostrarlos junto a su campo. */
function erroresDeFechas(error: unknown): ErroresFechas {
  const errores: ErroresFechas = {};
  if (error instanceof ErrorDeApi) {
    for (const { campo, mensaje } of error.detalles) {
      if (campo === 'creadoEn' || campo === 'cerradaEn' || campo === 'fechaLimite') {
        errores[campo] ??= mensaje;
      }
    }
  }
  return errores;
}

function PanelCrear({
  personas,
  puedeAdministrar,
  padreId = null,
  onCerrar,
  onAbrir,
}: Omit<PropsPanel, 'id'>) {
  const crear = useCrearTarea();
  // La madre de una subtarea nueva: su número y título para mostrarla, y su proyecto como valor inicial.
  // Desde su ficha ya está en caché; por enlace directo se espera un momento antes de dibujar el formulario
  // (sus valores iniciales se leen una sola vez). Si no existe o ya es subtarea (un solo nivel), se avisa y
  // se crea sin madre: la API la rechazaría igual con 400 `padre_invalido`.
  const madre = useTareaDetalle(padreId);
  const esperandoMadre = padreId !== null && madre.isPending;
  const leida = padreId !== null && madre.data ? madre.data : null;
  const datosMadre = leida && !leida.padre ? leida : null;
  const avisoMadre =
    padreId === null || esperandoMadre
      ? null
      : madre.isError
        ? 'No encontramos la tarea madre (puede que se haya eliminado). La tarea se creará sin madre.'
        : leida?.padre
          ? `${formatearNumero(leida.numero)} ya es subtarea de ${formatearNumero(leida.padre.numero)} y una subtarea no puede tener subtareas. La tarea se creará sin madre.`
          : null;
  return (
    <Contenedor titulo="Nueva tarea" onCerrar={onCerrar}>
      {avisoMadre && (
        <p className="formulario__aviso" role="alert">
          {avisoMadre}
        </p>
      )}
      {esperandoMadre ? (
        <p className="vista__vacio" role="status">
          Cargando la tarea madre…
        </p>
      ) : (
        <FormularioTarea
          esAdmin={puedeAdministrar}
          erroresApi={erroresDeFechas(crear.error)}
          valoresIniciales={
            datosMadre
              ? {
                  ...VALORES_NUEVA_TAREA,
                  padreId: String(datosMadre.id),
                  proyectoId: datosMadre.proyecto ? String(datosMadre.proyecto.id) : '',
                }
              : VALORES_NUEVA_TAREA
          }
          padre={datosMadre}
          personas={personas}
          etiquetaBoton="Crear tarea"
          etiquetaGuardando="Creando…"
          guardando={crear.isPending}
          onGuardar={(entrada) => crear.mutate(entrada, { onSuccess: onCerrar })}
          onCancelar={onCerrar}
          onAbrirParecida={onAbrir}
        />
      )}
    </Contenedor>
  );
}

function PanelDetalle({
  id,
  personas,
  puedeEscribir,
  puedeAdministrar,
  yoId,
  onCerrar,
  onAbrir,
  onNuevaSubtarea,
}: PropsPanel & { id: number }) {
  const cliente = useQueryClient();
  const detalle = useTareaDetalle(id);
  const eliminar = useEliminarTarea();
  const mostrarEsqueleto = useMostrarTrasRetardo(detalle.isPending);

  if (detalle.isPending || detalle.isError) {
    // Lo que ya se sabe por la lista del tablero se muestra al instante (barra y título).
    const conocida = tareaDeLasListas(cliente, id);
    const titulo = conocida ? (
      <>
        <IconoTipo tipo={conocida.tipo} />
        <span className="panel__id">{formatearNumero(conocida.numero)}</span>
      </>
    ) : detalle.isError ? (
      'Tarea'
    ) : (
      <span className={`panel__titulo-carga${mostrarEsqueleto ? '' : ' carga--espera'}`}>
        <Esqueleto forma="icono" />
        <Esqueleto className="panel__id-fantasma" />
        <span className="solo-lectores">Cargando tarea</span>
      </span>
    );
    const estado = conocida && (
      <span className="estado estado--lectura">{ETIQUETA_ESTADO[conocida.estado]}</span>
    );
    return (
      <Contenedor titulo={titulo} izquierda={estado} ampliable onCerrar={onCerrar}>
        {detalle.isError ? (
          <EstadoError
            texto={`No pudimos abrir ${conocida ? formatearNumero(conocida.numero) : 'esta tarea'}. Puede que se haya eliminado o que se cortara la conexión.`}
            reintentando={detalle.isFetching}
            onReintentar={() => void detalle.refetch()}
          >
            <button type="button" className="boton boton--con-icono" onClick={onCerrar}>
              Cerrar
            </button>
          </EstadoError>
        ) : (
          <FichaCargando titulo={conocida?.titulo} mostrar={mostrarEsqueleto} />
        )}
      </Contenedor>
    );
  }

  const tarea = detalle.data;
  const yoNombre = personas.find((persona) => persona.id === yoId)?.nombre;
  return (
    <Contenedor
      titulo={
        <>
          <IconoTipo tipo={tarea.tipo} />
          <span className="panel__id">{formatearNumero(tarea.numero)}</span>
        </>
      }
      izquierda={<SelectorEstado tarea={tarea} puedeEscribir={puedeEscribir} />}
      ampliable
      onCerrar={onCerrar}
    >
      <VistaTarea
        tarea={tarea}
        personas={personas}
        yoId={yoId}
        puedeEscribir={puedeEscribir}
        puedeAdministrar={puedeAdministrar}
        onAbrir={onAbrir}
        onNuevaSubtarea={onNuevaSubtarea}
      >
        <ListaAdjuntos
          tareaId={tarea.id}
          yoId={yoId}
          puedeEscribir={puedeEscribir}
          puedeAdministrar={puedeAdministrar}
        />
        <Comentarios
          tareaId={tarea.id}
          eventos={tarea.eventos}
          puedeEscribir={puedeEscribir}
          yoNombre={yoNombre}
        />
        <Historial tarea={tarea} />
        {puedeAdministrar && (
          <ZonaEliminar
            eliminando={eliminar.isPending}
            onEliminar={() => eliminar.mutate(tarea.id, { onSuccess: onCerrar })}
          />
        )}
      </VistaTarea>
    </Contenedor>
  );
}

const esPrioridad = (valor: string): valor is Prioridad =>
  (PRIORIDADES as readonly string[]).includes(valor);

/** Todo lo que pasó en la tarea salvo los comentarios (esos van en su propia sección), como línea de tiempo. */
function Historial({ tarea }: { tarea: TareaDetalle }) {
  const etiquetaPrioridad = useEtiquetaPrioridad();
  const eventos = tarea.eventos.filter((evento) => evento.tipo !== 'NOTA');

  return (
    <section className="historial" aria-labelledby="historial-titulo">
      <h3 id="historial-titulo" className="seccion-titulo">
        Historial
      </h3>
      <ul className="historial__lista">
        {[...eventos].reverse().map((evento) => (
          <li key={evento.id}>
            {evento.autor && (
              <strong title={evento.autor.nombre}>{nombreCorto(evento.autor.nombre)}</strong>
            )}{' '}
            <span className="historial__accion">{textoDeEvento(evento, etiquetaPrioridad)}</span>
            <time dateTime={evento.creadoEn}>{fechaHora(evento.creadoEn)}</time>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ZonaEliminar({ eliminando, onEliminar }: { eliminando: boolean; onEliminar: () => void }) {
  const [confirmando, setConfirmando] = useState(false);
  return (
    <div className="panel__peligro">
      {confirmando ? (
        <>
          <button
            type="button"
            className="boton boton--peligro boton--con-giro"
            disabled={eliminando}
            onClick={onEliminar}
          >
            {eliminando && <Giro />}
            {eliminando ? 'Eliminando…' : 'Sí, eliminar'}
          </button>
          <button type="button" className="boton" onClick={() => setConfirmando(false)}>
            No eliminar
          </button>
        </>
      ) : (
        <button type="button" className="boton boton--peligro" onClick={() => setConfirmando(true)}>
          Eliminar tarea
        </button>
      )}
    </div>
  );
}
